import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, Thermometer } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend
} from 'recharts';

// ==================== CALCULATION ENGINE ====================

function calcIEC(startTemp, setTemp) {
  const range = Math.abs(setTemp - startTemp);
  const t10 = startTemp + 0.1 * (setTemp - startTemp);
  const t90 = startTemp + 0.9 * (setTemp - startTemp);
  return { range, t10, t90, iecRange: Math.abs(t90 - t10) };
}

function generateIECData(startTemp, setTemp, rampRate) {
  const range = Math.abs(setTemp - startTemp);
  const heating = setTemp > startTemp;
  const t10 = startTemp + 0.1 * (setTemp - startTemp);
  const t90 = startTemp + 0.9 * (setTemp - startTemp);
  const iecRange = Math.abs(t90 - t10);
  const iecTime = iecRange / rampRate; // minutes for IEC portion

  // Total ramp takes longer because of the S-curve at start/end
  const totalRampTime = range / rampRate * 1.15;
  const soakTime = 15;
  const returnTime = totalRampTime * 1.1;
  const totalTime = totalRampTime + soakTime + returnTime + 10;

  const data = [];
  const dt = 0.3;

  for (let t = 0; t <= totalTime; t += dt) {
    let pv, sv;

    if (t <= totalRampTime) {
      // Heating ramp - S-curve (slow start, fast middle, slow end)
      sv = setTemp; // SV steps immediately
      // PV follows S-curve smoothly — no overshoot
      const pvProgress = Math.max(0, Math.min(1, (t - 1) / totalRampTime));
      const pvCurve = pvProgress < 0.5
        ? 2 * pvProgress * pvProgress
        : 1 - 2 * (1 - pvProgress) * (1 - pvProgress);
      pv = startTemp + pvCurve * range;
    } else if (t <= totalRampTime + soakTime) {
      // Soak period — PV holds steady at setTemp
      sv = setTemp;
      pv = setTemp;
    } else if (t <= totalRampTime + soakTime + returnTime) {
      // Return ramp — smooth S-curve back, no undershoot
      const returnProgress = (t - totalRampTime - soakTime) / returnTime;
      sv = startTemp; // SV steps back
      const pvCurve = returnProgress < 0.5
        ? 2 * returnProgress * returnProgress
        : 1 - 2 * (1 - returnProgress) * (1 - returnProgress);
      pv = setTemp + pvCurve * (startTemp - setTemp);
    } else {
      // Final settle — PV at startTemp
      sv = startTemp;
      pv = startTemp;
    }

    data.push({
      time: Math.round(t * 10) / 10,
      pv: Math.round(pv * 100) / 100,
      sv: Math.round(sv * 100) / 100
    });
  }

  return {
    data,
    iecTime: Math.round(iecTime * 10) / 10,
    iecRate: Math.round((iecRange / iecTime) * 100) / 100,
    t10, t90, iecRange,
    totalRampTime: Math.round(totalRampTime * 10) / 10
  };
}

function generateAverageData(startTemp, setTemp, rampRate) {
  const range = Math.abs(setTemp - startTemp);
  const heating = setTemp > startTemp;
  const avgTime = range / rampRate;
  const soakTime = 15;
  const returnTime = avgTime;
  const totalTime = avgTime + soakTime + returnTime + 10;

  const data = [];
  const dt = 0.3;

  for (let t = 0; t <= totalTime; t += dt) {
    let pv, sv;

    if (t <= avgTime) {
      sv = setTemp;
      // Average mode: PV follows S-curve smoothly — no overshoot
      const delayedProgress = Math.max(0, Math.min(1, (t - 0.5) / avgTime));
      const pvCurve = delayedProgress < 0.5
        ? 2 * delayedProgress * delayedProgress
        : 1 - 2 * (1 - delayedProgress) * (1 - delayedProgress);
      pv = startTemp + pvCurve * range;
    } else if (t <= avgTime + soakTime) {
      // Soak — PV holds steady at setTemp
      sv = setTemp;
      pv = setTemp;
    } else if (t <= avgTime + soakTime + returnTime) {
      // Return ramp — smooth S-curve back, no undershoot
      const rp = (t - avgTime - soakTime) / returnTime;
      sv = startTemp;
      const rpCurve = rp < 0.5 ? 2 * rp * rp : 1 - 2 * (1 - rp) * (1 - rp);
      pv = setTemp + rpCurve * (startTemp - setTemp);
    } else {
      // Final settle — PV at startTemp
      sv = startTemp;
      pv = startTemp;
    }

    data.push({
      time: Math.round(t * 10) / 10,
      pv: Math.round(pv * 100) / 100,
      sv: Math.round(sv * 100) / 100
    });
  }

  const calcRate = Math.round((range / avgTime) * 100) / 100;
  return { data, avgTime: Math.round(avgTime * 10) / 10, calcRate, range };
}

function generateLinearData(startTemp, setTemp, rampRate) {
  const range = Math.abs(setTemp - startTemp);
  const heating = setTemp > startTemp;
  const rampTime = range / rampRate;
  const soakTime = 15;
  const returnRampTime = rampTime;
  const overshootPct = 0.05; // 5% overshoot/undershoot
  const overshootDuration = 3; // minutes for overshoot arc
  const totalTime = rampTime + overshootDuration + soakTime + returnRampTime + overshootDuration + 5;

  const data = [];
  const dt = 0.3;

  for (let t = 0; t <= totalTime; t += dt) {
    let pv, sv;

    if (t <= rampTime) {
      // Linear ramp up — SV ramps linearly, PV follows SV closely
      const progress = t / rampTime;
      sv = startTemp + progress * (setTemp - startTemp);
      // PV tracks SV with tiny lag
      const pvProgress = Math.max(0, Math.min(1, (t - 0.3) / rampTime));
      pv = startTemp + pvProgress * (setTemp - startTemp);
    } else if (t <= rampTime + overshootDuration) {
      // Overshoot phase — PV overshoots past setTemp then returns to soak
      sv = setTemp;
      const osProgress = (t - rampTime) / overshootDuration;
      // PV rises past setTemp then settles back
      const overshoot = Math.sin(osProgress * Math.PI) * range * overshootPct * (heating ? 1 : -1);
      pv = setTemp + overshoot;
    } else if (t <= rampTime + overshootDuration + soakTime) {
      // Soak — PV holds steady at setTemp
      sv = setTemp;
      pv = setTemp;
    } else if (t <= rampTime + overshootDuration + soakTime + returnRampTime) {
      // Linear ramp back — PV follows SV linearly
      const rProgress = (t - rampTime - overshootDuration - soakTime) / returnRampTime;
      sv = setTemp + rProgress * (startTemp - setTemp);
      const pvProgress = Math.max(0, Math.min(1, (t - rampTime - overshootDuration - soakTime - 0.3) / returnRampTime));
      pv = setTemp + pvProgress * (startTemp - setTemp);
    } else if (t <= rampTime + overshootDuration + soakTime + returnRampTime + overshootDuration) {
      // Undershoot on return — PV undershoots past startTemp then returns
      sv = startTemp;
      const usProgress = (t - rampTime - overshootDuration - soakTime - returnRampTime) / overshootDuration;
      const undershoot = Math.sin(usProgress * Math.PI) * range * overshootPct * (heating ? -1 : 1);
      pv = startTemp + undershoot;
    } else {
      // Final settle — PV at startTemp
      sv = startTemp;
      pv = startTemp;
    }

    data.push({
      time: Math.round(t * 10) / 10,
      pv: Math.round(pv * 100) / 100,
      sv: Math.round(sv * 100) / 100
    });
  }

  const calcRate = Math.round((range / rampTime) * 100) / 100;
  return { data, rampTime: Math.round(rampTime * 10) / 10, calcRate, range };
}

// ==================== ANIMATED CHART ====================

const AnimatedChart = ({ data, title, refLines, startTemp, setTemp, animating }) => {
  const [visibleCount, setVisibleCount] = useState(animating ? 1 : data.length);

  useEffect(() => {
    if (!animating) {
      setVisibleCount(data.length);
      return;
    }
    setVisibleCount(1);
    const step = Math.max(1, Math.floor(data.length / 200));
    const interval = setInterval(() => {
      setVisibleCount(prev => {
        const next = prev + step;
        if (next >= data.length) {
          clearInterval(interval);
          return data.length;
        }
        return next;
      });
    }, 16);
    return () => clearInterval(interval);
  }, [data, animating]);

  const visibleData = data.slice(0, visibleCount);
  const minTemp = Math.min(startTemp, setTemp) - 15;
  const maxTemp = Math.max(startTemp, setTemp) + 15;
  const maxTime = data[data.length - 1]?.time || 100;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
      <h3 className="text-white text-sm font-semibold mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={visibleData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
          <XAxis
            dataKey="time"
            domain={[0, maxTime]}
            tickFormatter={v => `${Math.round(v)}m`}
            stroke="rgba(255,255,255,0.3)"
            tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
            label={{ value: 'Duration (min)', position: 'insideBottom', offset: -2, style: { fill: 'rgba(255,255,255,0.4)', fontSize: 11 } }}
          />
          <YAxis
            domain={[minTemp, maxTemp]}
            stroke="rgba(255,255,255,0.3)"
            tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
            label={{ value: 'Temperature (°C)', angle: -90, position: 'insideLeft', style: { fill: 'rgba(255,255,255,0.4)', fontSize: 11 } }}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1a2332', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12 }}
            labelFormatter={v => `${v} min`}
            formatter={(value, name) => [`${value}°C`, name === 'pv' ? 'TEMPPV' : 'TEMPSV']}
          />
          <Legend
            formatter={(value) => value === 'pv' ? 'TEMPPV' : 'TEMPSV'}
            wrapperStyle={{ fontSize: 12 }}
          />
          {refLines?.map((rl, i) => (
            <ReferenceLine
              key={i}
              y={rl.y}
              stroke={rl.color || 'rgba(255,100,255,0.5)'}
              strokeDasharray="6 3"
              label={{ value: rl.label, position: 'right', style: { fill: rl.color || 'rgba(255,100,255,0.7)', fontSize: 10 } }}
            />
          ))}
          <Line
            type="monotone"
            dataKey="sv"
            stroke="#60a5fa"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="pv"
            stroke="#ef4444"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== RESULT CARD ====================

const ResultCard = ({ method, icon, color, values }) => (
  <div className={`bg-white/5 border border-white/10 rounded-xl p-5`}>
    <div className="flex items-center gap-2 mb-4">
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center text-white text-sm font-bold`}>
        {icon}
      </div>
      <h3 className="text-white text-sm font-semibold">{method}</h3>
    </div>
    <div className="space-y-2">
      {values.map((v, i) => (
        <div key={i} className="flex justify-between items-center">
          <span className="text-gray-400 text-xs">{v.label}</span>
          <span className={`text-sm font-mono font-semibold ${v.highlight ? 'text-emerald-400' : 'text-white'}`}>{v.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// ==================== MAIN PAGE ====================

const RampCalculator = ({ user }) => {
  const navigate = useNavigate();
  const [startTemp, setStartTemp] = useState(-70);
  const [setTempValue, setSetTempValue] = useState(180);
  const [rampRate, setRampRate] = useState(3);
  const [results, setResults] = useState(null);
  const [animating, setAnimating] = useState(false);

  const calculate = useCallback(() => {
    if (startTemp === '' || setTempValue === '' || rampRate === '' || rampRate <= 0) return;
    const start = parseFloat(startTemp);
    const set = parseFloat(setTempValue);
    const rate = parseFloat(rampRate);
    if (isNaN(start) || isNaN(set) || isNaN(rate) || rate <= 0 || start === set) return;

    const iec = generateIECData(start, set, rate);
    const avg = generateAverageData(start, set, rate);
    const lin = generateLinearData(start, set, rate);
    const iecCalc = calcIEC(start, set);

    setResults({ iec, avg, lin, iecCalc, start, set, rate });
    setAnimating(true);
    // Turn off animation flag after charts complete
    setTimeout(() => setAnimating(false), 5000);
  }, [startTemp, setTempValue, rampRate]);

  const reset = () => {
    setStartTemp(-70);
    setSetTempValue(180);
    setRampRate(3);
    setResults(null);
    setAnimating(false);
  };

  return (
    <div className="min-h-screen" style={{ background: '#1a2332' }} data-testid="ramp-calculator-page">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">Ramp Calculator</h1>
              <p className="text-xs text-gray-500">IEC &middot; Average &middot; Linear</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Input Form */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Start Temperature (°C)</label>
              <input
                type="number"
                value={startTemp}
                onChange={e => setStartTemp(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="start-temp-input"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Set Temperature (°C)</label>
              <input
                type="number"
                value={setTempValue}
                onChange={e => setSetTempValue(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="set-temp-input"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Required Ramp Rate (°C/min)</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={rampRate}
                onChange={e => setRampRate(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="ramp-rate-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={calculate}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                data-testid="calculate-btn"
              >
                <Play className="w-4 h-4" /> Calculate
              </button>
              <button
                onClick={reset}
                className="bg-white/5 hover:bg-white/10 text-gray-400 px-3 py-2.5 rounded-lg"
                data-testid="reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {!results && (
          <div className="text-center py-20 text-gray-500">
            <Thermometer className="w-10 h-10 mx-auto mb-4 opacity-30" />
            <p className="text-sm">Enter temperatures and ramp rate, then click Calculate</p>
          </div>
        )}

        {results && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <ResultCard
                method="IEC 60068-3-5"
                icon="I"
                color="bg-violet-600"
                values={[
                  { label: 'Temperature Range', value: `${results.start}°C to ${results.set}°C` },
                  { label: '10% Point', value: `${Math.round(results.iecCalc.t10 * 10) / 10}°C` },
                  { label: '90% Point', value: `${Math.round(results.iecCalc.t90 * 10) / 10}°C` },
                  { label: 'IEC Range (80%)', value: `${Math.round(results.iecCalc.iecRange * 10) / 10}°C` },
                  { label: 'Time (10%→90%)', value: `${results.iec.iecTime} min` },
                  { label: 'Ramp Rate', value: `${results.iec.iecRate} °C/min`, highlight: true },
                ]}
              />
              <ResultCard
                method="Average Mode"
                icon="A"
                color="bg-amber-600"
                values={[
                  { label: 'Total Range', value: `${results.avg.range}°C` },
                  { label: 'Total Time', value: `${results.avg.avgTime} min` },
                  { label: 'Calculation', value: `${results.avg.range} ÷ ${results.avg.avgTime}` },
                  { label: 'Avg Ramp Rate', value: `${results.avg.calcRate} °C/min`, highlight: true },
                ]}
              />
              <ResultCard
                method="Linear Mode"
                icon="L"
                color="bg-cyan-600"
                values={[
                  { label: 'Total Range', value: `${results.lin.range}°C` },
                  { label: 'Ramp Time', value: `${results.lin.rampTime} min` },
                  { label: 'Calculation', value: `${results.lin.range} ÷ ${results.lin.rampTime}` },
                  { label: 'Linear Ramp Rate', value: `${results.lin.calcRate} °C/min`, highlight: true },
                ]}
              />
            </div>

            {/* Charts */}
            <div className="space-y-6">
              <AnimatedChart
                data={results.iec.data}
                title="IEC 60068-3-5 — Ramp Rate (10% to 90%)"
                startTemp={results.start}
                setTemp={results.set}
                animating={animating}
                refLines={[
                  { y: Math.round(results.iecCalc.t10 * 10) / 10, label: `10% (${Math.round(results.iecCalc.t10)}°C)`, color: 'rgba(255,100,255,0.6)' },
                  { y: Math.round(results.iecCalc.t90 * 10) / 10, label: `90% (${Math.round(results.iecCalc.t90)}°C)`, color: 'rgba(255,100,255,0.6)' },
                ]}
              />
              <AnimatedChart
                data={results.avg.data}
                title="Average Mode — Total Range ÷ Total Time"
                startTemp={results.start}
                setTemp={results.set}
                animating={animating}
                refLines={[]}
              />
              <AnimatedChart
                data={results.lin.data}
                title="Linear Mode — With Ramp Rate (overshoot/undershoot at soak transition)"
                startTemp={results.start}
                setTemp={results.set}
                animating={animating}
                refLines={[]}
              />
            </div>

            {/* Footer note */}
            <p className="text-gray-500 text-xs text-center mt-6 italic">
              Graphs shown for representation purpose only. Actual chamber performance may vary.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default RampCalculator;
